import React, { useState } from "react";
import Container from "./ui/Container";
import Button from "./ui/Button";
import { cn } from "@/lib/utils";
import { 
  Code, 
  ShoppingCart, 
  Cloud, 
  Bot, 
  RefreshCw, 
  ChevronRight, 
  MonitorSmartphone,
  Layers,
  Waypoints,
  Zap
} from "lucide-react";

const Services = () => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);

  const services = [
    {
      icon: <MonitorSmartphone className="w-6 h-6" />,
      title: "Custom Web Development",
      description: "A website that works for you—attract leads, impress clients, and grow your brand.",
      color: "bg-blue-50",
      iconColor: "text-tech-blue",
      borderColor: "border-blue-100",
      hoverBgColor: "group-hover:bg-blue-100/50"
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: "E-commerce Solutions",
      description: "Turn visitors into loyal customers with seamless, high-converting online stores.",
      color: "bg-green-50",
      iconColor: "text-green-600",
      borderColor: "border-green-100",
      hoverBgColor: "group-hover:bg-green-100/50"
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Salesforce Integrations",
      description: "Automate your sales process and never let a lead slip through the cracks.",
      color: "bg-indigo-50",
      iconColor: "text-indigo-600",
      borderColor: "border-indigo-100",
      hoverBgColor: "group-hover:bg-indigo-100/50"
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "AI-Powered Workflows",
      description: "AI call agents & automated processes mean less work for you and faster responses for clients.",
      color: "bg-purple-50",
      iconColor: "text-purple-600",
      borderColor: "border-purple-100",
      hoverBgColor: "group-hover:bg-purple-100/50"
    },
    {
      icon: <RefreshCw className="w-6 h-6" />,
      title: "Business Process Automation",
      description: "Say goodbye to manual errors and wasted hours—our automation solutions make business effortless.",
      color: "bg-amber-50",
      iconColor: "text-amber-600",
      borderColor: "border-amber-100",
      hoverBgColor: "group-hover:bg-amber-100/50"
    },
    {
      icon: <Code className="w-6 h-6" />,
      title: "Custom Software Solutions",
      description: "Tailored software solutions designed to meet your business's unique challenges and opportunities.",
      color: "bg-tech-cyan/10",
      iconColor: "text-tech-cyan",
      borderColor: "border-cyan-100",
      hoverBgColor: "group-hover:bg-cyan-100/50"
    }
  ];

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 relative overflow-hidden" id="services">
      {/* Background elements */}
      <div className="absolute -z-10 bottom-0 left-0 w-full h-full">
        <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-tech-cyan opacity-5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 left-1/3 w-80 h-80 bg-tech-blue opacity-5 rounded-full blur-3xl"></div>
      </div>
      
      <Container>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block px-3 py-1 rounded-full bg-tech-cyan/10 text-tech-cyan font-medium text-sm mb-4 animate-fade-in opacity-0" style={{animationDelay: "0.1s", animationFillMode: "forwards"}}>
            Our Services
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-tech-blue mb-6 animate-fade-in opacity-0" style={{animationDelay: "0.2s", animationFillMode: "forwards"}}>
            Innovative Solutions for Modern Businesses
          </h2>
          
          <p className="text-lg text-gray-700 animate-fade-in opacity-0" style={{animationDelay: "0.3s", animationFillMode: "forwards"}}>
            We deliver cutting-edge technology solutions that streamline operations, boost productivity, and drive growth for your business.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div 
              key={index}
              className={cn(
                "group relative bg-white rounded-xl p-6 border transition-all duration-300 animate-fade-in opacity-0 hover-scale",
                service.borderColor,
                hoveredIndex === index ? "shadow-lg" : "shadow-md"
              )}
              style={{animationDelay: `${0.3 + index * 0.1}s`, animationFillMode: "forwards"}}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(-1)}
            >
              <div className="absolute top-0 left-0 w-full h-full bg-white rounded-xl -z-10"></div>
              
              <div className={cn(
                "relative w-14 h-14 rounded-lg flex items-center justify-center mb-5 transition-all duration-300",
                service.color
              )}>
                <div className={cn("transition-all duration-300", service.iconColor)}>
                  {service.icon}
                </div>
                <div className={cn(
                  "absolute inset-0 rounded-lg opacity-0 transition-all duration-300",
                  service.hoverBgColor,
                  "group-hover:opacity-100"
                )}></div>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
              <p className="text-gray-600 mb-5">{service.description}</p>
              
              <div className="flex items-center text-tech-blue font-medium">
                <span>Learn more</span>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-16 text-center">
          <Button 
            size="lg" 
            className="shadow-lg shadow-tech-orange/10"
            onClick={handleContactClick}
          >
            Discover All Solutions
          </Button>
        </div>
        
        {/* Feature callout */}
        <div className="mt-24 relative glass-panel rounded-2xl overflow-hidden">
          <div 
            className="relative z-10 p-8 md:p-12 flex flex-col md:flex-row md:items-center" 
            style={{
              background: `linear-gradient(to right, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.8)), 
                          radial-gradient(circle at 30% 50%, rgba(30, 58, 138, 0.08), transparent 70%)`,
              backdropFilter: "blur(8px)"
            }}
          >
            <div className="md:w-2/3 mb-8 md:mb-0 md:pr-12">
              <h3 className="text-2xl md:text-3xl font-bold text-tech-blue mb-4">Unlock Your Business Potential</h3>
              <p className="text-gray-700 mb-6">
                Our solutions are designed to eliminate bottlenecks, reduce manual work, and create seamless workflows that scale with your business.
              </p>
              
              <div className="grid grid-cols-2 gap-4">
                {[
                  {icon: <Zap size={18} />, text: "Lightning-fast automation"},
                  {icon: <Layers size={18} />, text: "Seamless integrations"},
                  {icon: <Waypoints size={18} />, text: "Custom workflows"},
                  {icon: <Cloud size={18} />, text: "Cloud-based solutions"}
                ].map((item, i) => (
                  <div key={i} className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-tech-cyan/10 flex items-center justify-center mr-3 flex-shrink-0">
                      <div className="text-tech-cyan">
                        {item.icon}
                      </div>
                    </div>
                    <p className="text-sm text-gray-700">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="md:w-1/3">
              <Button 
                size="lg" 
                className="w-full justify-center shadow-lg shadow-tech-orange/20"
                onClick={handleContactClick}
              >
                Schedule a Demo
              </Button>
            </div>
          </div>
          
          {/* Decorative backgrounds */}
          <div className="absolute -z-10 top-0 right-0 w-full h-full opacity-10 pointer-events-none">
            <div className="absolute top-0 right-0 w-40 h-40 bg-tech-orange rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-60 h-60 bg-tech-blue rounded-full blur-3xl"></div>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Services;
